<template>
    <div
        v-qa="'cms-page'"
        class="page"
    >
        <PageContentLoader v-if="localLoader" />

        <PageErrorFiveHundred
            v-else-if="error"
            :errors="responseErrors"
        >
            <ThButton
                :disabled="localLoader"
                @click="executeQuery"
            >
                {{ t('globals.try_again') }}
            </ThButton>
        </PageErrorFiveHundred>

        <CmsBlocks
            v-else-if="localData && localData.pageByPath"
            :data="localData.pageByPath.blocks"
            page-type="page"
        />

        <PageErrorFourHundredFour v-else />
    </div>
</template>

<script lang="ts" setup>
import { computed, type Ref, ref, watch } from 'vue'
import { useRedirect, useSetStatus } from 'wucdbm-vite-vue-ssr'
import {
    type RouteLocationNormalized,
    type RouteParams,
    useRouter,
} from 'vue-router'
import { useI18n } from 'vue-i18n'

import { type PageByPathQuery } from '@/graphql/api'
import { usePageByPathQuery } from '@/graphql/api/cms/pageByPath.query.generated.ts'
import { useTranslator } from '@/i18n'
import { useMeta } from '@/composables/app/useMeta'
import { useCountryPlugin } from '@/plugins/country.ts'
import { useResponseError } from '@/helpers/errors'
import { defineWebPage, useSchemaOrg } from '@unhead/schema-org/vue'
import { useRoutes } from '@/router/routes'
import {
    engagementRingDesignStyleListPart,
    engagementRingStyleListPart,
    jewelleryDesignStylesPart,
    jewelleryGemstonePart,
    jewelleryProductStylesPart,
    jewelleryProductTypesListPart,
    metalCompoundSlugsListPart,
    metalSlugsListPart,
    monolithShapeListPart,
    weddingRingCategoriesPart,
    weddingRingStylePart,
} from '@/router'
import { getNumber } from '@/helpers'
import { useCurrencyInput } from '@/plugins/currency-input.ts'

import PageContentLoader from '@/components/ui/PageContentLoader.vue'
import PageErrorFourHundredFour from '@/components/PageErrorFourHundredFour.vue'
import CmsBlocks from '@/components/cms/CmsBlocks.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import PageErrorFiveHundred from '@/components/PageErrorFiveHundred.vue'

const { slug } = defineProps<{
    slug: string[]
}>()

useSchemaOrg([defineWebPage()])

const { t } = useI18n()

const redirect = useRedirect()
const router = useRouter()
const routes = useRoutes()

const { locale } = useTranslator()
const currency = useCurrencyInput()
const { countryCode } = useCountryPlugin()

const warrantyActivationPath = 'warranty-activation'
const isWarrantyActivation = computed(() =>
    slug.includes(warrantyActivationPath)
)
const path = computed(() =>
    isWarrantyActivation.value ? warrantyActivationPath : slug.join('/')
)
const variables = computed(() => ({
    path: path.value,
    locale: locale.value,
    currency: currency.value,
    country: countryCode.value!,
}))

const response = usePageByPathQuery({ variables })

const responseErrors = useResponseError(response)

const localData: Ref<PageByPathQuery | null> = ref(null)
const { fetching, stale, data, error, executeQuery } = response
const localLoader = ref(fetching.value)

watch(fetching, (n) => {
    if (n) {
        localLoader.value = true
    }
})

const { setStatus } = useSetStatus()

await response

async function redirectIfNecessary(): Promise<void> {
    // redirect if no warranty activation id
    if (isWarrantyActivation.value && !slug[1]) {
        return await redirect(router.resolve(routes.home()).href, 301)
    }

    if (data.value?.pageByPath) {
        localData.value = data.value
        localLoader.value = false
        return
    }

    const route = router.currentRoute.value
    const [page] = slug

    if ('ready-to-go' === page) {
        const [
            ,
            // skip page ^
            metal,
            page,
        ] = slug

        const params: RouteParams = {}

        if (metal) {
            params[metalSlugsListPart.name] = metal
        }

        const pageNum = getNumber(page)

        const hash = route.hash
        const routeWannaBe: Pick<RouteLocationNormalized, 'params' | 'hash'> = {
            params: params,
            hash: hash,
        }

        return await redirect(
            router.resolve(
                routes.readyToGoRings(
                    metalSlugsListPart.extractor(routeWannaBe),
                    undefined,
                    undefined,
                    Math.max(pageNum ?? 1, 1)
                )
            ).href,
            301
        )
    }

    if ('engagement-rings' === page) {
        const [
            ,
            // skip page ^
            metal,
            ringStyle,
            shape,
            designStyle,
            page,
        ] = slug

        const params: RouteParams = {}

        if (metal) {
            params[metalSlugsListPart.name] = metal
        }

        if (ringStyle) {
            params[engagementRingStyleListPart.name] = ringStyle
        }

        if (shape) {
            params[monolithShapeListPart.name] = shape
        }

        if (designStyle) {
            params[engagementRingDesignStyleListPart.name] = designStyle
        }

        const pageNum = getNumber(page)

        const hash = route.hash
        const routeWannaBe: Pick<RouteLocationNormalized, 'params' | 'hash'> = {
            params: params,
            hash: hash,
        }

        return await redirect(
            router.resolve(
                routes.engagementRings({
                    metal: metalSlugsListPart.extractor(routeWannaBe),
                    ringStyle:
                        engagementRingStyleListPart.extractor(routeWannaBe),
                    shape: monolithShapeListPart.extractor(routeWannaBe),
                    designStyle:
                        engagementRingDesignStyleListPart.extractor(
                            routeWannaBe
                        ),
                    page: Math.max(pageNum ?? 1, 1),
                })
            ).href,
            301
        )
    }

    if ('wedding-rings' === page) {
        const [
            ,
            // skip page ^
            category,
            metal,
            ringStyle,
            page,
        ] = slug

        const params: RouteParams = {}

        if (category) {
            params[weddingRingCategoriesPart.name] = category
        }

        if (metal) {
            params[metalSlugsListPart.name] = metal
        }

        if (ringStyle) {
            params[weddingRingStylePart.name] = ringStyle
        }

        const pageNum = getNumber(page)

        const hash = route.hash
        const routeWannaBe: Pick<RouteLocationNormalized, 'params' | 'hash'> = {
            params: params,
            hash: hash,
        }

        return await redirect(
            router.resolve(
                routes.weddingRings({
                    category: weddingRingCategoriesPart.extractor(routeWannaBe),
                    metal: metalSlugsListPart.extractor(routeWannaBe),
                    ringStyle: weddingRingStylePart.extractor(routeWannaBe),
                    page: Math.max(pageNum ?? 1, 1),
                })
            ).href,
            301
        )
    }

    if ('jewellery' === page) {
        const [
            ,
            // skip page ^
            productCategory,
            productStyle,
            gemstone,
            designStyle,
            metalSlug,
            page,
        ] = slug

        const params: RouteParams = {}

        if (productCategory) {
            params[jewelleryProductTypesListPart.name] = productCategory
        }

        if (productStyle) {
            params[jewelleryProductStylesPart.name] = productStyle
        }

        if (gemstone) {
            params[jewelleryGemstonePart.name] = gemstone
        }

        if (designStyle) {
            params[jewelleryDesignStylesPart.name] = designStyle
        }

        if (metalSlug) {
            params[metalCompoundSlugsListPart.name.param] = metalSlug
        }

        const pageNum = getNumber(page)

        const hash = route.hash
        const routeWannaBe: Pick<RouteLocationNormalized, 'params' | 'hash'> = {
            params: params,
            hash: hash,
        }

        return await redirect(
            router.resolve(
                routes.jewelleries({
                    productType:
                        jewelleryProductTypesListPart.extractor(routeWannaBe),
                    productStyle:
                        jewelleryProductStylesPart.extractor(routeWannaBe),
                    gemstone: jewelleryGemstonePart.extractor(routeWannaBe),
                    designStyle:
                        jewelleryDesignStylesPart.extractor(routeWannaBe),
                    metal: metalCompoundSlugsListPart.extractor(
                        routeWannaBe
                    )?.[0],

                    page: Math.max(pageNum ?? 1, 1),
                })
            ).href,
            301
        )
    }

    setStatus(404)
    localData.value = null
    localLoader.value = false
}

await redirectIfNecessary()

watch(data, redirectIfNecessary)

useMeta(
    {
        title: () => data.value?.pageByPath?.meta.title,
        description: () => data.value?.pageByPath?.meta.description,
    },
    data,
    fetching,
    stale
)
</script>
